/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Gnoment } from 'shared-ui/gnoment';
import { Icon, Button, FlexRow, FlexCol, VisualOnlyLabel } from 'sarsaparilla';
import { getValidationErrorStyle, NoQuotaMods } from '../../utils/utilsNew';
import { DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS, OVERRIDE_MAX_VALUE } from '../../constants';

export class GroupMemberCounterRuby extends React.Component {
    static propTypes = {
        guestTypes: PropTypes.arrayOf(PropTypes.shape({})),
        groupMembers: PropTypes.arrayOf(PropTypes.shape({})),
        onChange: PropTypes.func.isRequired,
        maxPetQuota: PropTypes.number,
        isBypassQuotaCheck: PropTypes.bool,
        entryDate: PropTypes.instanceOf(Gnoment),
        isQuotaModifyAllowed: PropTypes.bool,
        issuance: PropTypes.shape({
            id: PropTypes.string,
            group_size: PropTypes.number,
        }),
        permitId: PropTypes.string,
        validation: PropTypes.shape({}),
        onModifyDetails: PropTypes.func,
        modifyDetailsButtonText: PropTypes.string,
    };

    static defaultProps = {
        guestTypes: [
            {
                key: 'adult',
                type: 'Adult',
                description: 'ages 17+',
            },
            {
                key: 'child',
                type: 'Child',
                description: 'ages 0 - 16',
            },
            {
                key: 'dog',
                type: 'Dog',
                description: 'no age required',
            },
        ],
        memberLengthDisabled: false,
    };

    constructor(props) {
        super(props);
        this.handleGuestChange = this.handleGuestChange.bind(this);
        this.onChangeDetailsClick = this.onChangeDetailsClick.bind(this);
    }

    onChangeDetailsClick() {
        this.props.onModifyDetails(this.props.issuance);
    }

    getGroupText() {
        const issuanceGroupSize = this.props.isBypassQuotaCheck
            ? OVERRIDE_MAX_VALUE
            : this.props.issuance.group_size;

        return (
            <p data-testid="group-text">
                The quota for this permit is based on the number of people going on the
                trip. It can only be increased if there are enough spots available for the
                day. Selected <strong>group size is {issuanceGroupSize} members</strong>{' '}
                at this facility. There are a{' '}
                <strong>maximum of {this.props.maxPetQuota} pets</strong> allowed at this
                facilty.
            </p>
        );
    }

    handleGuestChange(e) {
        const { value: valueString, id: key } = e.target;
        const value = Number.parseInt(valueString, 10);
        const group_size = this.props.isBypassQuotaCheck
            ? OVERRIDE_MAX_VALUE
            : this.props.issuance.group_size;

        if (!this.props.isBypassQuotaCheck) {
            // if the number is already too big or negative.
            if (value > group_size || value < 0) {
                return;
            }
            // check if there are too many pets
            if (key === 'dog' && value > this.props.maxPetQuota) {
                // Ignore!
                return;
            }
        }

        const memberBreakdown = this.props.guestTypes.reduce((acc, type) => {
            acc[type.key] = 0;
            return acc;
        }, {});

        this.props.groupMembers.forEach((member) => {
            if (member.isAdult) {
                memberBreakdown.adult += 1;
            } else if (member.isChild) {
                memberBreakdown.child += 1;
            } else if (member.isDog) {
                memberBreakdown.dog += 1;
            }
        });

        // apply desired change here
        memberBreakdown[key] = value;

        // new sum!
        const sum = memberBreakdown.adult + memberBreakdown.child + memberBreakdown.dog;
        if (sum <= group_size) {
            // good change!
            const newGroupMembers = [];
            Object.keys(memberBreakdown).forEach((type) => {
                for (let i = 0; i < memberBreakdown[type]; i += 1) {
                    const newMember = { startDate: this.props.entryDate };
                    if (type === 'adult') {
                        newMember.isAdult = true;
                    } else if (type === 'child') {
                        newMember.isChild = true;
                    } else if (type === 'dog') {
                        newMember.isDog = true;
                    }
                    newGroupMembers.push(newMember);
                }
            });

            this.props.onChange(newGroupMembers);
        } else {
            // alert the user of a bad change?
        }
    }

    renderChangeDetails() {
        if (this.props.isBypassQuotaCheck) {
            return null;
        }
        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                        className="change-details-link"
                    >
                        {this.props.modifyDetailsButtonText}
                    </Button>
                </div>
            );
        }
        return NoQuotaMods;
    }

    renderGuestTypes() {
        const memberBreakdown = this.props.guestTypes.reduce((acc, type) => {
            acc[type.key] = 0;
            return acc;
        }, {});

        this.props.groupMembers.forEach((member) => {
            if (member.isAdult) {
                memberBreakdown.adult += 1;
            } else if (member.isChild) {
                memberBreakdown.child += 1;
            } else if (member.isDog) {
                memberBreakdown.dog += 1;
            }
        });

        const types = this.props.guestTypes.map((type) => {
            return (
                <FlexRow className="per-group-member-type" key={type.key}>
                    <FlexCol md={4} alignSelf="center">
                        <label htmlFor={type.key}>
                            {type.type} ({type.description})
                        </label>
                    </FlexCol>
                    <FlexCol md={2}>
                        <input
                            id={type.key}
                            type="number"
                            value={memberBreakdown[type.key]}
                            min="0"
                            onChange={this.handleGuestChange}
                        />
                    </FlexCol>
                </FlexRow>
            );
        });

        return <div className="per-group-member-type-list">{types}</div>;
    }

    renderGroupSize() {
        const group_size = this.props.isBypassQuotaCheck
            ? OVERRIDE_MAX_VALUE
            : this.props.issuance.group_size;
        const currentSize = this.props.groupMembers.length;
        const validationStyle = getValidationErrorStyle(
            this.props.validation,
            DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS,
            'rec-error'
        );
        const groupSizeMatchCheck =
            this.props.validation &&
            this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]
                ? this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]
                : null;
        const validationMessage =
            validationStyle &&
            groupSizeMatchCheck &&
            this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]?.message
                ? this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]?.message
                : groupSizeMatchCheck;
        return (
            <div className="per-total-count-wrapper">
                <span className={`per-total-count ${validationStyle}`}>
                    Total Group Size: <b>{`${currentSize} / ${group_size}`}</b>
                </span>

                {validationStyle && <div className="validation">{validationMessage}</div>}
            </div>
        );
    }

    render() {
        return (
            <div
                data-testid="group-member-counter-ruby"
                className="per-group-member-form per-section-group"
            >
                <div className="rec-order-detail-subsection-title">Group Size</div>
                {this.getGroupText()}
                {this.renderGroupSize()}
                <FlexRow>
                    <FlexCol md={4}>
                        <VisualOnlyLabel>Type</VisualOnlyLabel>
                    </FlexCol>
                    <FlexCol md={2}>
                        <VisualOnlyLabel isRequired>Quantity</VisualOnlyLabel>
                    </FlexCol>
                </FlexRow>
                {this.renderGuestTypes()}
                {this.renderChangeDetails()}
            </div>
        );
    }
}

export default GroupMemberCounterRuby;
